/**
 * MobileManagersListModal
 *
 * Mobile full-screen MUI Dialog replacement for the desktop ManagersListModal.
 * Displays team managers as card-based list with add/remove functionality.
 * Slide-up transition, sticky AppBar with search.
 */
import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Slide,
  Fade,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { useDispatch, useSelector } from "react-redux";
import {
  getTeamsManagers,
  setTeamManagers,
  removeTeamManager,
} from "../../../components/teams/teamsSlice";
import Langs, { Localize } from "../../../app/lang/langs";
import { showErrorToast } from "../../../app/utils/helpers";
import MobileAddTeamManagersModal from "./MobileAddTeamManagersModal";

/* ------------------------------------------------------------------ */
/*  Transition                                                         */
/* ------------------------------------------------------------------ */
const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* ------------------------------------------------------------------ */
/*  Style constants                                                    */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

const cardSx = {
  mx: 1.5,
  mb: 1,
  borderRadius: "18px",
  backgroundColor: "rgba(255,255,255,0.88)",
  backdropFilter: "blur(20px) saturate(1.3)",
  WebkitBackdropFilter: "blur(20px) saturate(1.3)",
  border: "1px solid rgba(255,144,21,0.06)",
  boxShadow: "0 4px 16px rgba(0,0,0,0.04), 0 1px 4px rgba(0,0,0,0.03), 0 0 0 0.5px rgba(255,255,255,0.5) inset",
  transition: TRANSITION_SMOOTH,
  "&:active": {
    transform: "scale(0.98)",
  },
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/**
 * @param {Object}   props
 * @param {boolean}  props.visible     - controls dialog open state
 * @param {Function} props.setVisible  - toggle visibility
 * @param {string|number} props.idTeam - team id for fetching managers
 * @param {string}   props.teamName    - team name to display in header
 */
export default function MobileManagersListModal({ visible, setVisible, idTeam, teamName }) {
  const dispatch = useDispatch();
  const managersList = useSelector((state) => state.teams.teamManagers);

  const [addModalVisibility, setAddModalVisibility] = useState(false);
  const [searchText, setSearchText] = useState("");

  // Fetch managers when opened
  useEffect(() => {
    if (visible && idTeam) {
      dispatch(getTeamsManagers({ id_team: idTeam }));
    } else {
      dispatch(setTeamManagers([]));
    }
  }, [visible, idTeam, dispatch]);

  // ---- Remove manager ----
  const handleRemoveManager = async (manager) => {
    if (!idTeam || !manager.id_collab) return;

    const data = {
      id_team: idTeam,
      id_collab: manager.id_collab,
    };

    try {
      const response = await dispatch(removeTeamManager(data));

      if (response.payload.responseData && response.payload.responseData.status === 1) {
        const updatedManagers = managersList.filter((m) => m.id_collab !== manager.id_collab);
        dispatch(setTeamManagers(updatedManagers));
      } else {
        showErrorToast(response.payload?.message || "Échec de la suppression du manager");
      }
    } catch (error) {
      console.error("Error removing manager:", error);
      showErrorToast("Une erreur est survenue lors de la suppression du manager");
    }
  };

  // ---- Filter by search ----
  const filteredManagers = useMemo(() => {
    if (!searchText.trim()) return managersList || [];
    const q = searchText.toLowerCase().trim();
    return (managersList || []).filter(
      (m) =>
        (m.prenom && m.prenom.toLowerCase().includes(q)) ||
        (m.nom && m.nom.toLowerCase().includes(q)) ||
        (m.email && m.email.toLowerCase().includes(q))
    );
  }, [managersList, searchText]);

  // ---- Close handler ----
  const handleClose = () => {
    if (!visible) return;
    setVisible(false);
    setSearchText("");
  };

  // ---- Render manager card ----
  const renderManagerCard = (manager, index) => {
    const fullName = `${manager.prenom || ""} ${manager.nom || ""}`.trim();

    return (
      <Fade in timeout={150 + index * 40} key={manager.id_collab || index}>
        <Card sx={cardSx} variant="outlined">
          <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
            {/* Name row */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <PersonOutlineIcon sx={{ fontSize: 20, color: "rgba(0,0,0,0.45)" }} />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  color: "rgba(0,0,0,0.87)",
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fullName || "—"}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleRemoveManager(manager)}
                sx={{
                  minWidth: 44,
                  minHeight: 44,
                  borderRadius: "12px",
                  color: "#db5856",
                  transition: TRANSITION_SMOOTH,
                  "&:active": { transform: "scale(0.92)" },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Email */}
            {manager.email && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 3.25, mb: 0.5 }}>
                <EmailOutlinedIcon sx={{ fontSize: 14, color: "rgba(0,0,0,0.35)" }} />
                <Typography
                  sx={{
                    fontSize: "0.82rem",
                    color: "rgba(0,0,0,0.5)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {manager.email}
                </Typography>
              </Box>
            )}

            {/* Type badge */}
            {manager.type && (
              <Box sx={{ ml: 3.25 }}>
                <Chip
                  icon={<BadgeOutlinedIcon sx={{ fontSize: 14 }} />}
                  label={manager.type}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: "0.75rem",
                    height: 26,
                    borderRadius: "8px",
                    fontWeight: 600,
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Fade>
    );
  };

  return (
    <>
      <Dialog
        fullScreen
        open={!!visible}
        onClose={handleClose}
        TransitionComponent={SlideUp}
        sx={{
          zIndex: 1400,
          "& .MuiDialog-paper": {
            background: "linear-gradient(180deg, #f8f9fb 0%, #f0f1f5 100%)",
          },
        }}
      >
        {/* Sticky AppBar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            backgroundColor: "rgba(255,255,255,0.82)",
            backdropFilter: "blur(24px) saturate(1.5)",
            WebkitBackdropFilter: "blur(24px) saturate(1.5)",
            borderBottom: "1px solid rgba(0,0,0,0.06)",
            color: "rgba(0,0,0,0.87)",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "2px",
              background: "linear-gradient(90deg, transparent, rgba(255,144,21,0.3), transparent)",
            },
          }}
        >
          <Toolbar sx={{ minHeight: 56, px: 1 }}>
            <IconButton
              edge="start"
              onClick={handleClose}
              sx={{
                minWidth: 44,
                minHeight: 44,
                borderRadius: "12px",
                color: "rgba(0,0,0,0.7)",
                transition: TRANSITION_SMOOTH,
                "&:active": { transform: "scale(0.94)" },
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box sx={{ flex: 1, ml: 0.5 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  letterSpacing: "-0.01em",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {teamName || <Langs str="MANAGERS" />}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "rgba(0,0,0,0.5)" }}>
                {filteredManagers.length} {Localize("MANAGERS") || "managers"}
              </Typography>
            </Box>
            <IconButton
              onClick={() => setAddModalVisibility(true)}
              sx={{
                minWidth: 44,
                minHeight: 44,
                borderRadius: "12px",
                background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                color: "#fff",
                transition: TRANSITION_SMOOTH,
                boxShadow: "0 3px 10px rgba(255,144,21,0.3)",
                "&:active": { transform: "scale(0.92)" },
                "&:hover": { background: "linear-gradient(135deg, #FFC100 0%, #FF9015 100%)" },
              }}
            >
              <AddIcon />
            </IconButton>
          </Toolbar>

          {/* Search bar */}
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={Localize("SEARCH") + "..."}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20, color: "rgba(0,0,0,0.35)" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  backgroundColor: "rgba(255,255,255,0.5)",
                  backdropFilter: "blur(8px)",
                  fontSize: "16px",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
                  transition: TRANSITION_SMOOTH,
                  "& fieldset": { borderColor: "rgba(255,144,21,0.08)" },
                  "&.Mui-focused fieldset": {
                    borderColor: "rgba(255,144,21,0.4)",
                    boxShadow: "0 0 0 3px rgba(255,144,21,0.08)",
                  },
                },
                "& input": { fontSize: "16px", py: 1 },
              }}
            />
          </Box>
        </AppBar>

        {/* Body */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            pb: 3,
            pt: 1,
            mt: 0,
            WebkitOverflowScrolling: "touch",
          }}
        >
          {filteredManagers.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
              }}
            >
              <Typography sx={{ color: "rgba(0,0,0,0.4)", fontSize: "0.95rem" }}>
                Aucun manager trouvé.
              </Typography>
            </Box>
          ) : (
            filteredManagers.map((manager, i) => renderManagerCard(manager, i))
          )}
        </Box>
      </Dialog>

      <MobileAddTeamManagersModal
        visible={addModalVisibility}
        setVisible={setAddModalVisibility}
        prevManagers={managersList}
        idTeam={idTeam}
      />
    </>
  );
}
